<template>
  <v-dialog v-if="featureEnabled" :value="open" persistent width="520" @keydown.esc="handleCancel">
    <v-card class="afld-card pa-0" data-testid="add-family-level-dialog">
      <div class="afld-header">
        <span class="afld-featured-icon" data-testid="afld-featured-icon">
          <svg
            class="afld-ladder-icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6 20V4M18 20V4M6 8h12M6 14h12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <button
          type="button"
          class="afld-close"
          aria-label="Close Add Level dialog"
          data-testid="afld-close"
          :disabled="busy"
          @click="handleCancel"
        >
          <svg
            class="afld-close__icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M18 6 6 18M6 6l12 12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <h2 id="afld-title" class="afld-title" data-testid="afld-title">Add Level</h2>

      <div class="afld-body">
        <div class="afld-field">
          <label class="afld-label" for="afld-name-input" data-testid="afld-name-label">
            Name
            <span class="afld-required" aria-hidden="true">*</span>
          </label>
          <input
            id="afld-name-input"
            v-model.trim="name"
            class="afld-input"
            :class="{ 'afld-input--error': nameError }"
            type="text"
            placeholder="e.g. Senior Engineer"
            autocomplete="off"
            required
            :aria-required="true"
            :aria-invalid="!!nameError"
            :disabled="busy"
            data-testid="afld-name-input"
          />
          <p v-if="nameError" class="afld-error" data-testid="afld-name-error">{{ nameError }}</p>
        </div>

        <div class="afld-field">
          <label class="afld-label" for="afld-descriptor-input" data-testid="afld-descriptor-label">
            Descriptor
          </label>
          <textarea
            id="afld-descriptor-input"
            v-model.trim="descriptor"
            class="afld-input afld-textarea"
            :class="{ 'afld-input--error': descriptorError }"
            placeholder="e.g. Owns outcomes for a single team; occasional cross-team input."
            autocomplete="off"
            :aria-required="false"
            :aria-invalid="!!descriptorError"
            :disabled="busy"
            data-testid="afld-descriptor-input"
          />
          <p v-if="descriptorError" class="afld-error" data-testid="afld-descriptor-error">
            {{ descriptorError }}
          </p>
        </div>
      </div>

      <div class="afld-divider" />

      <div class="afld-footer">
        <button
          type="button"
          class="afld-btn afld-btn--secondary"
          data-testid="afld-cancel"
          :disabled="busy"
          @click="handleCancel"
        >
          Cancel
        </button>
        <button
          type="button"
          class="afld-btn afld-btn--primary"
          :class="{ 'afld-btn--disabled': saveDisabled }"
          :disabled="saveDisabled"
          :aria-busy="busy ? 'true' : 'false'"
          data-testid="afld-save"
          @click="handleSave"
        >
          <span
            v-if="busy"
            class="afld-spinner"
            aria-hidden="true"
            data-testid="afld-save-spinner"
          />
          <span>{{ busy ? "Saving" : "Save Level" }}</span>
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "AddFamilyLevelDialog",
};
</script>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";

import { LD_FLAG_IDEA_MDP_581 } from "@components/constants/launchDarklyFlagKeys";

/**
 * ER-11644 — AddFamilyLevelDialog (Organization > Job Families detail, "+ Add Level"
 * on the Levels table).
 *
 * Untitled-UI modal for adding a new Level to a Job Family. Exactly two authored
 * fields — Name (required) and Descriptor (optional), both empty every time the
 * dialog opens (ER-11790 makes Descriptor optional; Name stays required). No
 * status, ordinal, Organization Object Code, Enterprise Contribution Anchor or
 * Career Stream — a Level carries nothing else (Job Family rules §3). The ordinal
 * is never authored here: on save the new Level stacks on top of the ladder and
 * takes max(ordinal) + 1 automatically (rules §3.1/§10.2). Level names are not
 * unique within a Family, so there is no client-side duplicate check.
 *
 * Controlled presentational component: the parent owns `open`, passes `busy` while
 * the create request is in flight, and `errors` with any server-side field errors
 * keyed by `name` / `descriptor`. Emits `save` with the trimmed `{ name, descriptor }`
 * payload (only once Name is non-empty; Descriptor may be blank) and `cancel` when
 * Cancel, the close X, or Escape dismisses the dialog. No API calls inside the
 * component.
 *
 * Add and Edit are separate components (rules §6/§10.2) — this is add-only.
 * EditFamilyLevelDialog is the sibling for editing an existing Level; keep field
 * labels, placeholders and layout identical across the two.
 *
 * Shipped behind the linked idea flag (idea-MDP-581, "Job Family — Discipline &
 * Career-Ladder Grouping for Positions"), the same kill switch as AddJobFamilyDialog
 * and JobFamiliesList. Renders by default; set the flag to false to keep the dialog
 * closed until a screen wires it (ER-11650).
 */
const props = defineProps({
  /** Boolean that controls dialog visibility (v-model:open). */
  open: {
    type: Boolean,
    default: false,
  },
  /** True while the parent's create request is in flight. */
  busy: {
    type: Boolean,
    default: false,
  },
  /** Server field errors keyed by `name` / `descriptor`. */
  errors: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["update:open", "save", "cancel"]);

const featureEnabled = ref(false);
const proxy = getCurrentInstance()?.proxy;

const name = ref("");
const descriptor = ref("");

const nameError = computed(() => props.errors?.name || "");
const descriptorError = computed(() => props.errors?.descriptor || "");

const saveDisabled = computed(() => {
  return busyOrEmpty();
});

function busyOrEmpty() {
  if (props.busy) {
    return true;
  }
  return name.value.trim().length === 0;
}

/** Both fields reset to empty every time the dialog opens (add-only, never pre-filled). */
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      name.value = "";
      descriptor.value = "";
    }
  }
);

function handleSave() {
  if (busyOrEmpty()) {
    return;
  }
  emit("save", { name: name.value.trim(), descriptor: descriptor.value.trim() });
}

function handleCancel() {
  if (props.busy) {
    return;
  }
  emit("update:open", false);
  emit("cancel");
}

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, true)) !== false;
  } catch {
    // Kill-switch lookup failures never lock the dialog shut.
    featureEnabled.value = true;
  }
});
</script>

<style scoped>
.afld-card {
  background-color: #ffffff;
  /* !important overrides Vuetify's default v-card radius/elevation classes, which
     otherwise win over this scoped rule on the generated card markup. */
  border-radius: 12px !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
}

.afld-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 0 24px;
}

.afld-featured-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  color: #667085;
}

.afld-ladder-icon {
  display: block;
}

.afld-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 8px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #667085;
  cursor: pointer;
}

.afld-close:hover {
  background: #f2f4f7;
}

.afld-close:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.afld-close:focus-visible,
.afld-input:focus-visible,
.afld-btn:focus-visible {
  outline: 2px solid #6941c6;
  outline-offset: 1px;
}

.afld-title {
  margin: 0;
  padding: 0 24px 20px 24px;
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.afld-body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 4px 24px 24px 24px;
}

.afld-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.afld-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.afld-required {
  color: #f04438;
}

.afld-input {
  box-sizing: border-box;
  width: 100%;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  line-height: 19px;
  color: #101828;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.afld-input::placeholder {
  color: #667085;
}

.afld-textarea {
  height: 58px;
  resize: none;
}

.afld-input--error {
  border-color: #f04438;
}

.afld-error {
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: #f04438;
}

.afld-divider {
  height: 1px;
  margin: 8px 0 0 0;
  background: #eaecf0;
}

.afld-footer {
  display: flex;
  gap: 12px;
  padding: 24px;
}

.afld-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 1 1 0;
  height: 44px;
  padding: 10px 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.afld-btn--secondary {
  background: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.afld-btn--primary {
  background: #6941c6;
  border: 1px solid #6941c6;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.afld-btn--disabled,
.afld-btn:disabled {
  background: #f2f4f7;
  border: none;
  color: #98a2b3;
  box-shadow: none;
  cursor: not-allowed;
}

.afld-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: afld-spin 0.7s linear infinite;
}

@keyframes afld-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
